/*
 * Purpose: Goal-driven mission planning — GOAL block SSOT; no template clone by keyword
 * Last modified: 2026-07-10 | Agent
 * Completeness: 95
 */
import type { PlannedStep } from '@/services/autoblocks/types';

/** Block categories the planner assigns — derived from GOAL, not template names. */
export type BlockCategoryRole =
  | 'trigger'
  | 'fetch'
  | 'transform'
  | 'decide'
  | 'act'
  | 'notify'
  | 'store'
  | 'mcp';

export interface GoalStepMeta {
  blockIntent: string;
  taskOutcome: string;
  categoryRole: BlockCategoryRole;
}

const genStepId = () => `step-${Math.random().toString(36).slice(2, 9)}`;

function step(
  label: string,
  type: PlannedStep['type'],
  subtype: string,
  rationale: string,
  meta: GoalStepMeta,
  config: Record<string, unknown> = {}
): PlannedStep {
  return {
    id: genStepId(),
    blockId: `${type}.${subtype}`,
    label,
    type,
    subtype,
    rationale,
    blockIntent: meta.blockIntent,
    taskOutcome: meta.taskOutcome,
    categoryRole: meta.categoryRole,
    config: {
      name: label,
      blockIntent: meta.blockIntent,
      taskOutcome: meta.taskOutcome,
      categoryRole: meta.categoryRole,
      contextPurpose: meta.blockIntent,
      ...config,
    },
  };
}

/** Missions compile from the user's outcome — never auto-clone average-user templates. */
export function shouldCloneTemplate(_matchedTemplateId?: string): boolean {
  return false;
}

/**
 * Derive ordered steps from the stated outcome (GOAL source text).
 * Connectors like LinkedIn are capabilities — each block still carries intent + task outcome for MCP.
 */
export function buildGoalDrivenSteps(rawOutcome: string, interpretedGoal?: string): PlannedStep[] {
  const lower = rawOutcome.toLowerCase();
  const goal = (interpretedGoal || rawOutcome).trim();
  const steps: PlannedStep[] = [];

  const wantsEmail =
    /\b(email|gmail|inbox|job alert|alert me when|when .* emails?)\b/i.test(lower);
  const wantsLinkedIn = /\blinkedin\b/i.test(lower);
  const wantsSearch = /\b(search|find|discover|scan|look for|pull)\b/i.test(lower);
  const wantsApply = /\b(apply|submit)\b/i.test(lower);
  const wantsTrack = /\b(track|log|record|spreadsheet|tracker|status)\b/i.test(lower);
  const wantsNotify = /\b(notify|alert|tell me|send me|remind)\b/i.test(lower);
  const wantsAi = /\b(resume|cover letter|tailor|summarize|score|rank|gpt|ai)\b/i.test(lower);

  if (wantsEmail) {
    steps.push(
      step(
        'Watch matching email',
        'trigger',
        'gmail_inbound',
        'Start when email matches the mission trigger — configured from the GOAL.',
        {
          blockIntent: `Detect inbound email events relevant to: ${goal}`,
          taskOutcome: 'Normalized email payload (from, subject, body snippet, links) for downstream blocks.',
          categoryRole: 'trigger',
        },
        { connectorId: 'ab.gmail', source: 'gmail_api' }
      )
    );
  } else {
    steps.push(
      step(
        'Start on demand',
        'trigger',
        'manual',
        'Operator or schedule starts the run — map wiring begins at START → GOAL.',
        {
          blockIntent: `Begin work toward: ${goal}`,
          taskOutcome: 'Run context seeded with mission parameters from the GOAL block.',
          categoryRole: 'trigger',
        }
      )
    );
  }

  if (wantsLinkedIn && wantsSearch) {
    steps.push(
      step(
        'Fetch from LinkedIn (MCP)',
        'tool',
        'mcp_tool',
        'Use MCP tools to query LinkedIn — intent comes from GOAL, not a reused template.',
        {
          blockIntent: `From LinkedIn, gather data needed for: ${goal}`,
          taskOutcome:
            'Structured records (roles, companies, URLs, dates) matching GOAL filters — ready for scoring or storage.',
          categoryRole: 'mcp',
        },
        {
          connectorId: 'ab.mcp',
          mcpCapability: 'tools/call',
          capabilityId: 'mcp.call_tool',
          mcpToolName: 'linkedin_search',
          mcpGoal: goal,
        }
      )
    );
  } else if (wantsLinkedIn) {
    steps.push(
      step(
        'LinkedIn capability (MCP)',
        'tool',
        'mcp_tool',
        'LinkedIn actions are goal-scoped — specify what to read/write via MCP per GOAL.',
        {
          blockIntent: `Execute LinkedIn operations required by: ${goal}`,
          taskOutcome: 'LinkedIn API/MCP response aligned to the GOAL (profiles, messages, or job metadata).',
          categoryRole: 'mcp',
        },
        {
          connectorId: 'ab.mcp',
          mcpCapability: 'tools/call',
          capabilityId: 'mcp.call_tool',
          mcpGoal: goal,
        }
      )
    );
  }

  if (wantsAi) {
    steps.push(
      step(
        'Score or tailor with AI',
        'tool',
        'transform',
        'AI step serves the GOAL — fact-only; never invent credentials or experience.',
        {
          blockIntent: `Apply AI to intermediate data to advance: ${goal}`,
          taskOutcome: 'Ranked, summarized, or tailored text/JSON consumable by the next block.',
          categoryRole: 'transform',
        },
        { transform: '// Shape input using GOAL criteria only\nreturn input;' }
      )
    );
  }

  if (wantsApply) {
    steps.push(
      step(
        'Apply or submit',
        'tool',
        'http',
        'External write — requires human approval when elevated/critical.',
        {
          blockIntent: `Complete the apply/submit portion of: ${goal}`,
          taskOutcome: 'Confirmation payload (application id, status, timestamp) stored on success.',
          categoryRole: 'act',
        },
        { method: 'POST', riskLevel: 'elevated' }
      )
    );
  }

  if (wantsTrack) {
    steps.push(
      step(
        'Persist mission results',
        'action',
        'storage',
        'Retain outputs in mission storage — Postgres is SSOT for mission-generated data.',
        {
          blockIntent: `Record progress and artifacts for: ${goal}`,
          taskOutcome: 'Durable log row keyed by mission/run with human-readable summary fields.',
          categoryRole: 'store',
        },
        { key: 'mission_results' }
      )
    );
  }

  if (wantsNotify || steps.length <= 2) {
    steps.push(
      step(
        'Notify operator',
        'action',
        'notification',
        'Surface completion or exceptions to the operator.',
        {
          blockIntent: `Tell the user what happened while pursuing: ${goal}`,
          taskOutcome: 'Notification with summary + link to mission results / next action.',
          categoryRole: 'notify',
        },
        { message: `Mission update: ${goal}` }
      )
    );
  }

  return steps;
}

/** Copy goal-step metadata onto assembled workflow node configs. */
export function applyGoalMetadataToNodeConfig(
  step: PlannedStep,
  base: Record<string, unknown>
): Record<string, unknown> {
  const intent = step.blockIntent ?? step.rationale;
  const outcome = step.taskOutcome ?? '';
  const stepKey = String(step.config.refineNodeId ?? step.id);
  return {
    ...base,
    name: base.name ?? step.label,
    blockIntent: intent,
    taskOutcome: outcome,
    categoryRole: step.categoryRole,
    contextPurpose: intent,
    mcpGoal: step.config.mcpGoal ?? (step.subtype === 'mcp_tool' ? intent : undefined),
    _autoBlocksRationale: step.rationale,
    _autoBlocksStepId: stepKey,
  };
}
